import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Chip,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import BugReportIcon from '@mui/icons-material/BugReport';
import axios from 'axios';

// Get environment variables with fallbacks
// The app talks to the API via the relative '/api' base (CRA proxy in dev,
// Firebase Hosting rewrite in prod) — diagnostics must test the same origin.
const API_URL = '/api';
const CONTENT_TYPE = process.env.REACT_APP_CONTENT_TYPE || 'text/plain';
const WITH_CREDENTIALS = process.env.REACT_APP_WITH_CREDENTIALS !== 'false';

const LoginDiagnostics = () => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [diagnosticResults, setDiagnosticResults] = useState({
    environment: {},
    cookies: {},
    corsTest: {},
    csrfTest: {},
  });

  const handleOpen = () => {
    setOpen(true);
    runDiagnostics();
  };

  const handleClose = () => {
    setOpen(false);
  };

  const runDiagnostics = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Environment check
      const environment = {
        browserInfo: {
          userAgent: navigator.userAgent,
          cookiesEnabled: navigator.cookieEnabled,
        },
        appConfig: {
          apiUrl: API_URL,
          withCredentials: WITH_CREDENTIALS,
          nodeEnv: process.env.NODE_ENV,
        },
        origins: {
          currentOrigin: window.location.origin,
          targetApi: API_URL,
          isSameOrigin: window.location.origin === API_URL,
        }
      };
      
      // Cookie check
      const cookies = {
        hasCookies: !!document.cookie,
        count: document.cookie ? document.cookie.split(';').length : 0,
        names: document.cookie 
          ? document.cookie.split(';').map(c => c.trim().split('=')[0]) 
          : [],
      };
      
      // CORS test - basic OPTIONS preflight
      let corsTest = { success: false, error: null, details: null };
      try {
        console.log('Running CORS preflight test...');
        const corsResponse = await axios({
          url: `${API_URL}/auth/csrf-token`,
          method: 'OPTIONS',
          headers: {
            'Access-Control-Request-Method': 'GET',
            'Access-Control-Request-Headers': 'content-type',
            'Origin': window.location.origin
          }
        });
        
        corsTest = {
          success: true,
          status: corsResponse.status,
          statusText: corsResponse.statusText,
          headers: {
            'access-control-allow-origin': corsResponse.headers['access-control-allow-origin'],
            'access-control-allow-methods': corsResponse.headers['access-control-allow-methods'],
            'access-control-allow-headers': corsResponse.headers['access-control-allow-headers'],
            'access-control-allow-credentials': corsResponse.headers['access-control-allow-credentials'],
          }
        };
      } catch (error) {
        corsTest = { 
          success: false, 
          error: error.message,
          details: error.response ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers
          } : 'No response received',
          isCORSError: error.message.includes('Network Error')
        };
      }
      
      // CSRF token test
      let csrfTest = { success: false, error: null, details: null };
      try {
        console.log('Running CSRF token test...');
        const csrfResponse = await axios.get(`${API_URL}/auth/csrf-token`, { 
          withCredentials: WITH_CREDENTIALS,
          headers: {
            'Content-Type': CONTENT_TYPE
          }
        });
        
        csrfTest = {
          success: true,
          tokenReceived: !!csrfResponse.data.csrfToken,
          tokenLength: csrfResponse.data.csrfToken ? csrfResponse.data.csrfToken.length : 0,
          headers: {
            'content-type': csrfResponse.headers['content-type'],
            'set-cookie': csrfResponse.headers['set-cookie'] ? 'Present' : 'Not present',
            'access-control-allow-origin': csrfResponse.headers['access-control-allow-origin'],
            'access-control-allow-credentials': csrfResponse.headers['access-control-allow-credentials'],
          }
        };
      } catch (error) {
        csrfTest = { 
          success: false, 
          error: error.message,
          details: error.response ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers
          } : 'No response received',
          isCORSError: error.message.includes('Network Error')
        };
      }
      
      setDiagnosticResults({
        environment,
        cookies,
        corsTest,
        csrfTest
      });
    } catch (err) {
      setError(`Failed to run diagnostics: ${err.message}`);
      console.error('Diagnostic error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to render test status
  const renderTestStatus = (test) => {
    if (test.skipped) {
      return <Chip 
        icon={<WarningIcon />} 
        label="Skipped" 
        color="warning" 
        size="small"
      />;
    }
    
    return test.success 
      ? <Chip 
          icon={<CheckCircleIcon />} 
          label="Success" 
          color="success" 
          size="small"
        /> 
      : <Chip 
          icon={<ErrorIcon />} 
          label="Failed" 
          color="error" 
          size="small"
        />;
  };

  return (
    <>
      <Button 
        startIcon={<BugReportIcon />}
        onClick={handleOpen} 
        variant="text" 
        color="inherit" 
        size="small"
        sx={{ mt: 1, color: 'text.secondary' }}
      >
        Connection Diagnostics
      </Button>
      
      <Dialog 
        open={open} 
        onClose={handleClose} 
        fullWidth 
        maxWidth="md"
        PaperProps={{
          sx: { minHeight: '60vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Connection Diagnostics</Typography>
            <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary" paragraph>
              This tool diagnoses network connectivity, CORS settings, and cookie behavior to help troubleshoot login issues.
            </Typography>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" color="primary">
                {isLoading ? 'Running diagnostics...' : 'Diagnostic Results'}
              </Typography>
              <Button 
                variant="outlined" 
                size="small"
                onClick={runDiagnostics}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={16} /> : null}
              >
                {isLoading ? 'Running...' : 'Run Again'}
              </Button>
            </Box>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" my={8}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Environment Information */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Environment Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="API URL" 
                        secondary={diagnosticResults.environment.appConfig?.apiUrl || 'Not available'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="With Credentials Setting" 
                        secondary={String(diagnosticResults.environment.appConfig?.withCredentials) || 'Not available'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Current Origin" 
                        secondary={diagnosticResults.environment.origins?.currentOrigin || 'Not available'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Target API Origin" 
                        secondary={(() => {
                          const apiUrl = diagnosticResults.environment.origins?.targetApi;
                          if (!apiUrl) return 'Not available';
                          try {
                            return new URL(apiUrl).origin;
                          } catch {
                            return apiUrl + ' (cannot parse as URL)';
                          }
                        })()} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Same Origin Status" 
                        secondary={(() => {
                          const isSameOrigin = diagnosticResults.environment.origins?.isSameOrigin;
                          return isSameOrigin === true ? 'Same origin' : isSameOrigin === false ? 'Cross-origin' : 'Unknown';
                        })()} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Cookies Enabled" 
                        secondary={diagnosticResults.environment.browserInfo?.cookiesEnabled ? 'Yes' : 'No'} 
                      />
                    </ListItem>
                  </List>
                </AccordionDetails>
              </Accordion>
              
              {/* Cookies Information */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Cookies</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Has Cookies" 
                        secondary={diagnosticResults.cookies?.hasCookies ? 'Yes' : 'No'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Cookie Count" 
                        secondary={diagnosticResults.cookies?.count || 0} 
                      />
                    </ListItem>
                    {diagnosticResults.cookies?.hasCookies && (
                      <ListItem>
                        <ListItemText 
                          primary="Cookie Names" 
                          secondary={diagnosticResults.cookies?.names?.join(', ') || 'None'} 
                        />
                      </ListItem>
                    )}
                  </List>
                </AccordionDetails>
              </Accordion>
              
              {/* CORS Test */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
                    <Typography variant="subtitle1">CORS Test</Typography>
                    {diagnosticResults.corsTest && renderTestStatus(diagnosticResults.corsTest)}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {diagnosticResults.corsTest?.success ? (
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="Status" 
                          secondary={`${diagnosticResults.corsTest.status} ${diagnosticResults.corsTest.statusText}`} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Allow Origin" 
                          secondary={diagnosticResults.corsTest.headers?.['access-control-allow-origin'] || 'Not set'} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Allow Methods" 
                          secondary={diagnosticResults.corsTest.headers?.['access-control-allow-methods'] || 'Not set'} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Allow Headers" 
                          secondary={diagnosticResults.corsTest.headers?.['access-control-allow-headers'] || 'Not set'} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Allow Credentials" 
                          secondary={diagnosticResults.corsTest.headers?.['access-control-allow-credentials'] || 'Not set'} 
                        />
                      </ListItem>
                    </List>
                  ) : (
                    <Box>
                      <Alert severity="error" sx={{ mb: 2 }}>
                        CORS Test Failed: {diagnosticResults.corsTest?.error || 'Unknown error'}
                      </Alert>
                      
                      {diagnosticResults.corsTest?.isCORSError && (
                        <Alert severity="info">
                          This appears to be a CORS-related error. The backend may not be configured
                          to allow requests from {window.location.origin}.
                        </Alert>
                      )}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
              
              {/* CSRF Token Test */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
                    <Typography variant="subtitle1">CSRF Token Test</Typography>
                    {diagnosticResults.csrfTest && renderTestStatus(diagnosticResults.csrfTest)}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {diagnosticResults.csrfTest?.success ? (
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="Token Received" 
                          secondary={diagnosticResults.csrfTest.tokenReceived ? 'Yes' : 'No'} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Token Length" 
                          secondary={diagnosticResults.csrfTest.tokenLength || 0} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Set-Cookie Header" 
                          secondary={diagnosticResults.csrfTest.headers?.['set-cookie'] || 'Not set'} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Allow Origin" 
                          secondary={diagnosticResults.csrfTest.headers?.['access-control-allow-origin'] || 'Not set'} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Allow Credentials" 
                          secondary={diagnosticResults.csrfTest.headers?.['access-control-allow-credentials'] || 'Not set'} 
                        />
                      </ListItem>
                    </List>
                  ) : (
                    <Box>
                      <Alert severity="error" sx={{ mb: 2 }}>
                        CSRF Test Failed: {diagnosticResults.csrfTest?.error || 'Unknown error'}
                      </Alert>
                      
                      {diagnosticResults.csrfTest?.isCORSError && (
                        <Alert severity="info">
                          This appears to be a CORS-related error. Check that your backend is correctly configured
                          to allow requests from {window.location.origin}.
                        </Alert>
                      )}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LoginDiagnostics;